/**
 * Feature E03-F02 — "Pre-Kickoff анализ + 3 драфта письма" request/response
 * contracts. 1:1 port of the legacy n8n `pre_kickoff_analysis` sub-flow
 * (docs/source-n8n/reference/AI.md: AI: Fetch Context → Build Prompt → Run
 * Analysis → Parse & Save → Save Artifacts → Return Result; canonical prompt
 * `pre_kickoff_analysis` in docs/source-n8n/ai_prompts.md).
 *
 * Single source of truth for:
 *   - POST /projects/:id/ai/pre_kickoff_analysis  (T03 route + T04 web client)
 *   - the analysis + 3-draft response shape the SPA renders (T04 panel)
 *   - the 4 artifact `content` shapes persisted via UPSERT (T02)
 *
 * IDOR invariant (carried over unchanged): `userId` / `created_by` is NEVER part
 * of any *input* schema here — the api derives the actor from the validated
 * session, so a request can never forge it (AI.md gotcha: legacy
 * `body.userId || 'system'`).
 *
 * Lives in its OWN file (not re-exported from the shared barrel by this agent);
 * the serial Wire pass adds the `export *` line to `packages/shared/src/index.ts`.
 */
import { z } from 'zod';
// ---------------------------------------------------------------------------
// Enums (Parse & Save mapping — AI.md §"AI: Parse & Save")
// ---------------------------------------------------------------------------
/**
 * Information-sufficiency classification. Lower-cased on parse; defaults to
 * `hybrid` when the model omits/garbles it (legacy Parse & Save default).
 */
export const PRE_KICKOFF_CLASSIFICATIONS = ['sufficient', 'insufficient', 'hybrid'];
export const preKickoffClassificationSchema = z.enum(PRE_KICKOFF_CLASSIFICATIONS);
/**
 * Recommended email draft. Defaults to `C` (the hybrid variant) when missing —
 * the legacy Parse & Save default.
 */
export const PRE_KICKOFF_RECOMMENDATIONS = ['A', 'B', 'C'];
export const preKickoffRecommendationSchema = z.enum(PRE_KICKOFF_RECOMMENDATIONS);
/** The three draft option labels (A: meeting, B: details, C: hybrid). */
export const PRE_KICKOFF_DRAFT_OPTIONS = ['A', 'B', 'C'];
/** Artifact `type` per draft option (legacy `pre_kickoff_draft_<A|B|C>`). */
export const PRE_KICKOFF_DRAFT_TYPES = {
    A: 'pre_kickoff_draft_A',
    B: 'pre_kickoff_draft_B',
    C: 'pre_kickoff_draft_C',
};
/** The analysis artifact `type` (legacy `analysis`). */
export const PRE_KICKOFF_ANALYSIS_TYPE = 'analysis';
// ---------------------------------------------------------------------------
// Analysis content (project_artifacts.content for type='analysis')
// ---------------------------------------------------------------------------
/**
 * The merged analysis object the legacy `AI: Parse & Save` builds and UPSERTs as
 * the `analysis` artifact content (AI.md §Parse & Save step 7 + acceptance #2:
 * the extended Section 29.4 fields are persisted). All extended fields are
 * optional strings — the model uses `''` when not found (prompt rule).
 */
export const preKickoffAnalysisSchema = z.object({
    /** 2-3 sentence summary of the inquiry (rendered in the Pre-Kickoff panel). */
    summary: z.string().default(''),
    classification: preKickoffClassificationSchema.default('hybrid'),
    recommendation: preKickoffRecommendationSchema.default('C'),
    /** Bullet key-points (analysis.key_points OR analysis.gaps). */
    key_points: z.array(z.string()).default([]),
    // --- Extended Section 29.4 fields (company + role brief) ---
    company_name: z.string().default(''),
    industry: z.string().default(''),
    company_type: z.string().default(''),
    offices: z.string().default(''),
    products: z.string().default(''),
    position_title: z.string().default(''),
    country: z.string().default(''),
    scope: z.string().default(''),
    key_requirements: z.string().default(''),
    role_goal: z.string().default(''),
});
// ---------------------------------------------------------------------------
// Draft content (project_artifacts.content for type='pre_kickoff_draft_<X>')
// ---------------------------------------------------------------------------
/**
 * One email draft variant. `to` is ALWAYS the project contact email (AI.md
 * gotcha: "draft.to всегда = contactEmail"); `body` is plain text with NO
 * signature block (prompt rule "Do NOT include a signature block").
 */
export const preKickoffDraftSchema = z.object({
    /** Human label (Kick-off Meeting / Request Details / Hybrid). */
    title: z.string().default(''),
    /** Recipient — the project contact's email (server-injected, not from LLM). */
    to: z.string().default(''),
    subject: z.string().default(''),
    body: z.string().default(''),
});
/** The three drafts keyed by option (A/B/C). */
export const preKickoffDraftsSchema = z.object({
    A: preKickoffDraftSchema,
    B: preKickoffDraftSchema,
    C: preKickoffDraftSchema,
});
// ---------------------------------------------------------------------------
// Route request / response (POST /projects/:id/ai/pre_kickoff_analysis)
// ---------------------------------------------------------------------------
/**
 * Request body. Intentionally empty of identity — `projectId` comes from the URL
 * and the actor from the session (IDOR rule). A `session_token` may ride in the
 * body for legacy transport parity, but is never read for authorization here.
 */
export const preKickoffAnalysisRequestSchema = z
    .object({
    session_token: z.string().min(1).optional(),
    /**
     * PK-QA-1 soft-confirm override. When the readiness check finds no analyzable
     * material the route answers 412 `reason:'nothing_to_analyze'`; the client
     * re-issues the same request with `confirmed:true` to run anyway. Never
     * overrides the hard 400 `VALIDATION_ERROR` (missing client/role/contact).
     */
    confirmed: z.boolean().optional(),
})
    .partial();
/**
 * Success response (AI: Return Result shape:
 * `{ success, analysis, drafts, projectId }`).
 */
export const preKickoffAnalysisResponseSchema = z.object({
    success: z.literal(true).optional(), // web client strips `success`; backend still sends it
    projectId: z.number().int(),
    analysis: preKickoffAnalysisSchema,
    drafts: preKickoffDraftsSchema,
});
// ---------------------------------------------------------------------------
// PK4 (E03-F02-T08) — edit the persisted analysis brief
//   PATCH /projects/:id/analysis  (backend route + web client)
// ---------------------------------------------------------------------------
/**
 * The subset of analysis fields a human may edit inline in the Pre-Kickoff panel
 * (PK4). These are the free-text BRIEF fields the panel renders read-only today
 * (summary + Company/Position cards + key-points). Deliberately EXCLUDED:
 *   - `company_name` (→ project.client_name) and `position_title`
 *     (→ project.role_name): the brief derives these with project-scalar
 *     precedence, so they are edited via PATCH /projects/:id, NOT here.
 *   - `classification` / `recommendation`: internal enums that route the 3
 *     drafts — not brief content, not user-editable.
 * So the editable set is exactly the 10 brief fields below.
 */
export const PRE_KICKOFF_ANALYSIS_EDITABLE_FIELDS = [
    'summary',
    'industry',
    'company_type',
    'offices',
    'products',
    'country',
    'scope',
    'key_requirements',
    'role_goal',
    'key_points',
];
/**
 * PATCH /projects/:id/analysis request body — a PARTIAL patch over the editable
 * brief fields (any subset; the server merges it over the existing `analysis`
 * artifact content). `.strict()` rejects unknown keys so a client cannot smuggle
 * `classification` / `company_name` through this route. At least one field must be
 * present — an empty patch is a 400 (nothing to save). An omitted key is left
 * untouched (patch semantics); a present key with `''` / `[]` clears that field.
 */
export const preKickoffAnalysisEditSchema = preKickoffAnalysisSchema
    .pick({
    summary: true,
    industry: true,
    company_type: true,
    offices: true,
    products: true,
    country: true,
    scope: true,
    key_requirements: true,
    role_goal: true,
    key_points: true,
})
    .partial()
    .strict()
    .refine((p) => Object.keys(p).length > 0, {
    message: 'Empty patch: send at least one analysis field',
});
/**
 * PATCH /projects/:id/analysis success response — the FULL merged analysis after
 * the edit (the exact shape the panel already renders via buildPreKickoffBrief).
 */
export const preKickoffAnalysisEditResponseSchema = z.object({
    success: z.literal(true).optional(),
    projectId: z.number().int(),
    analysis: preKickoffAnalysisSchema,
});
//# sourceMappingURL=prekickoff-analysis.js.map